/**
 * Module-scope readers for the active country map bootstrapped by
 * `layouts/app.blade.php` into `window.__init.countries`. Mirrors the
 * Inertia shared prop `init.countries` so modules that evaluate before
 * React mounts (Redux init state, regex compilation) stay DB-driven.
 *
 * If you are inside a React component, prefer `useCountries()` /
 * `useEUIsoCodes()` from `@/hooks/useCountries` — they handle the
 * Inertia-vs-window fallback uniformly and pick up shortName overrides.
 */
type CountryMap = Record<string, App.Data.CountryData>;

function readMap(): CountryMap {
    if (typeof window === 'undefined') return {};
    return window.__init?.countries ?? {};
}

/**
 * ISO codes whose `region` reads 'EU' but which we treat as non-EU for
 * business purposes. Mirrors PHP CountryLookup::NON_EU_MEMBER_ISOS —
 * keep both in sync if the business rule changes.
 */
const NON_EU_MEMBER_ISOS = new Set<string>(['GB']);

/**
 * Single source of truth for "EU member" semantics on the FE.
 * Use everywhere instead of inlining `region === 'EU'`.
 */
export function isEuMember(
    iso: string | null | undefined,
    region: string | null | undefined,
): boolean {
    return region === 'EU' && !!iso && !NON_EU_MEMBER_ISOS.has(iso);
}

/** ISO codes of active EU-member countries (excludes UK). */
export function euIsoCodes(): string[] {
    return Object.entries(readMap())
        .filter(([iso, c]) => isEuMember(iso, c.region))
        .map(([iso]) => iso);
}

/** Unique currency-sign glyphs across active countries (e.g. ['£','$','€']). */
export function currencySigns(): string[] {
    const signs = Object.values(readMap())
        .map((c) => c.currency_sign)
        .filter((s): s is string => Boolean(s));
    return [...new Set(signs)];
}
